import React, { useContext, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Button,
  Divider,
  Link as MuiLink,
} from "@mui/material";
import { UserContext } from "../../auth/auth-context";
import useLoadReduxStoreData from "../../../redux/reducers/loadReduxStoreData";
import { BusinessQueryNames } from "../../../graphql/queries";
import { useNavigate } from "react-router-dom";
import { useAuthStatus } from "../../../util/useAuthStatus";

interface BusinessSubmitConfirmationProps {
  open: boolean;
  onClose: () => void;
}

const BusinessSubmitConfirmation: React.FC<BusinessSubmitConfirmationProps> = ({
  open,
  onClose,
}) => {
  const { providerUser } = useContext(UserContext);
  const { isLoggedIn } = useAuthStatus();

  const { refetch: reloadBusinesses } = useLoadReduxStoreData(
    BusinessQueryNames.GET_MY_BUSINESSES,
    { enabled: isLoggedIn }
  );
  const navigate = useNavigate();
  const [linkLoading, setLinkLoading] = useState(false);
  const [closing, setClosing] = useState(false);

  // Reload before closing
  const handleClose = async () => {
    setClosing(true);
    try {
      await reloadBusinesses();
      navigate("/biz/bookingtypes");
    } catch (err) {
      console.error("Failed to reload businesses:", err);
    }
    setClosing(false);
    onClose();
  };

  // Reload before navigating via the link
  const handleBookingTypeClick = async (
    e: React.MouseEvent<HTMLAnchorElement, MouseEvent>
  ) => {
    e.preventDefault();
    setLinkLoading(true);
    try {
      await reloadBusinesses();
      navigate("/biz/bookingtypes");
    } catch (err) {
      console.error("Failed to reload businesses before navigating:", err);
    } finally {
      setLinkLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Business Created!</DialogTitle>
      <DialogContent>
        <Typography variant="body1" sx={{ mb: 2 }}>
          Congratulations, {providerUser.username}! Your business has been
          successfully created.
        </Typography>
        <Divider sx={{ mb: 2 }} />
        <Typography variant="body2" sx={{ mb: 1 }}>
          A confirmation email has been sent to your inbox.
        </Typography>
        <Typography variant="body2">
          Next step:{" "}
          <MuiLink
            href="/biz/bookingtypes"
            underline="hover"
            onClick={handleBookingTypeClick}
            sx={{
              pointerEvents: linkLoading ? "none" : "auto",
              opacity: linkLoading ? 0.6 : 1,
            }}
          >
            {linkLoading ? "Loading..." : "Create your booking types here"}
          </MuiLink>
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} variant="contained" disabled={closing}>
          {closing ? "Closing..." : "Close"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BusinessSubmitConfirmation;
